// src/utils/mnemonicUtils.js
import { ethers } from 'ethers';

/**
 * Utility functions for handling BIP-39 mnemonics (seed phrases)
 * for BitDog Wallet
 */

/**
 * Generate a random BIP-39 mnemonic (seed phrase)
 * @param {number} wordCount - Number of words (12, 15, 18, 21, or 24)
 * @returns {string} - Space-separated mnemonic phrase
 */
export const generateMnemonic = (wordCount = 12) => {
  // Validate word count
  if (![12, 15, 18, 21, 24].includes(wordCount)) {
    throw new Error('Invalid word count. Must be 12, 15, 18, 21, or 24');
  }
  
  try {
    // Generate a new random mnemonic with ethers.js
    // For Ethers v6, we just need to use Wallet.createRandom() and get the mnemonic
    const randomWallet = ethers.Wallet.createRandom();
    return randomWallet.mnemonic?.phrase || '';
  } catch (error) {
    console.error('Error generating mnemonic:', error);
    throw new Error('Failed to generate secure mnemonic');
  }
};

/**
 * Validate a BIP-39 mnemonic (seed phrase)
 * @param {string} mnemonic - Space-separated mnemonic phrase
 * @returns {boolean} - True if valid
 */
export const validateMnemonic = (mnemonic) => {
  try {
    // In Ethers v6, we check for valid mnemonic by trying to create a wallet from it
    ethers.Wallet.fromPhrase(mnemonic);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get the default derivation path for a specific HD wallet type
 * UPDATED: Now uses MetaMask's derivation path pattern
 * @param {string} coinType - Coin type (e.g., 'ethereum', 'bitrock')
 * @param {number} addressIndex - Address index (default: 0)
 * @returns {string} - Derivation path
 */
export const getDerivationPath = (coinType = 'ethereum', addressIndex = 0) => {
  // BIP-44 derivation path pattern: m / purpose' / coin_type' / account' / change / address_index
  // MetaMask style: Fixed account at 0, and increment address_index
  
  let coinTypeIndex;
  switch (coinType.toLowerCase()) {
    case 'ethereum':
    case 'eth':
      coinTypeIndex = 60; // Ethereum
      break;
    case 'bitrock':
    case 'brock':
      coinTypeIndex = 60; // Using Ethereum's for BitRock (since it's EVM compatible)
      break;
    default:
      coinTypeIndex = 60; // Default to Ethereum
  }
  
  // Return MetaMask-compatible derivation path
  // Note: Account is fixed at 0, and we increment address_index
  return `m/44'/${coinTypeIndex}'/0'/0/${addressIndex}`;
};

/**
 * Generate a wallet from a mnemonic with a specific address index
 * UPDATED: Now uses MetaMask's derivation pattern
 * @param {string} mnemonic - BIP-39 mnemonic phrase
 * @param {string} customPath - Custom derivation path (optional)
 * @param {number} addressIndex - Index of the address to derive (default: 0)
 * @returns {Object} - { address, privateKey }
 */
export const getWalletFromMnemonic = (mnemonic, customPath = null, addressIndex = 0) => {
  try {
    // Validate the mnemonic
    if (!validateMnemonic(mnemonic)) {
      throw new Error('Invalid mnemonic');
    }
    
    // In Ethers v6, we use Wallet.fromPhrase directly to create a wallet from a mnemonic
    // This creates the default wallet at m/44'/60'/0'/0/0
    const wallet = ethers.Wallet.fromPhrase(mnemonic);
    
    // If we're derivation address 0, we can just return the wallet directly
    if (addressIndex === 0 && !customPath) {
      return {
        address: wallet.address,
        privateKey: wallet.privateKey,
        path: "m/44'/60'/0'/0/0", // Default Ethereum path
        index: 0
      };
    }
    
    // Use MetaMask style path: increment address index, fixed account=0
    const path = customPath || `m/44'/60'/0'/0/${addressIndex}`;
    
    // Note: In Ethers v6, we need to use the HDNodeWallet directly
    // with a different approach for derivation
    const hdNode = ethers.HDNodeWallet.fromMnemonic(
      ethers.Mnemonic.fromPhrase(mnemonic),
      path
    );
    
    return {
      address: hdNode.address,
      privateKey: hdNode.privateKey,
      path: path,
      index: addressIndex
    };
  } catch (error) {
    console.error('Error deriving wallet from mnemonic:', error);
    throw new Error('Failed to generate wallet from mnemonic');
  }
};

/**
 * Derive multiple wallets from a single mnemonic
 * UPDATED: Now uses MetaMask's derivation pattern
 * @param {string} mnemonic - BIP-39 mnemonic phrase
 * @param {number} count - Number of wallets to derive
 * @param {string} coinType - Coin type (e.g., 'ethereum', 'bitrock')
 * @returns {Array<Object>} - Array of wallet objects: [{ address, privateKey, path, index }]
 */
export const deriveMultipleWallets = (mnemonic, count = 1, coinType = 'ethereum') => {
  try {
    const wallets = [];
    
    for (let i = 0; i < count; i++) {
      // For each address index, create a wallet with MetaMask's path pattern
      // Keep account index fixed at 0, and increment address index
      const path = `m/44'/60'/0'/0/${i}`;
      
      // For the first wallet (i=0), we can use the default derivation
      if (i === 0) {
        const wallet = ethers.Wallet.fromPhrase(mnemonic);
        wallets.push({
          address: wallet.address,
          privateKey: wallet.privateKey,
          path: path,
          index: i,
          name: `Account ${i + 1}` // Default name
        });
      } else {
        // For other indices, use the HDNodeWallet with specific path
        const hdNode = ethers.HDNodeWallet.fromMnemonic(
          ethers.Mnemonic.fromPhrase(mnemonic),
          path
        );
        
        wallets.push({
          address: hdNode.address,
          privateKey: hdNode.privateKey,
          path: path,
          index: i,
          name: `Account ${i + 1}` // Default name
        });
      }
    }
    
    return wallets;
  } catch (error) {
    console.error('Error deriving multiple wallets:', error);
    throw new Error('Failed to derive wallets from mnemonic');
  }
};

/**
 * Check if a string looks like a private key (for backward compatibility)
 * @param {string} input - String to check
 * @returns {boolean} - True if it looks like a private key
 */
export const isPrivateKey = (input) => {
  // Check if it's a hex string with or without 0x prefix
  if (input.startsWith('0x')) {
    return /^0x[0-9a-fA-F]{64}$/.test(input);
  } else {
    return /^[0-9a-fA-F]{64}$/.test(input);
  }
};

/**
 * Get a private key from a mnemonic and index
 * UPDATED: Now uses MetaMask's derivation pattern
 * @param {string} mnemonic - BIP-39 mnemonic phrase
 * @param {number} index - Address index
 * @returns {string} - Private key
 */
export const getPrivateKeyFromMnemonic = (mnemonic, index = 0) => {
  if (index === 0) {
    // For index 0, use the default wallet derived from the mnemonic
    const wallet = ethers.Wallet.fromPhrase(mnemonic);
    return wallet.privateKey;
  } else {
    // For other indices, use MetaMask's derivation path pattern
    const path = `m/44'/60'/0'/0/${index}`;
    const hdNode = ethers.HDNodeWallet.fromMnemonic(
      ethers.Mnemonic.fromPhrase(mnemonic),
      path
    );
    return hdNode.privateKey;
  }
};